/* eslint-disable formatjs/no-literal-string-in-jsx */
import { Fragment, useState } from "react";

// oxlint-disable-next-line no-restricted-imports
import MenuIcon from "@mui/icons-material/Menu";
import { alpha } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { Theme } from "@mui/material/styles";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { makeStyles } from "@mui/styles";

import { Icon } from "@/shared/components/common/Icon/Icon";
import { trackHelpIcon } from "@/shared/services/mixpanel";

import { useToggle } from "../../../../shared/hooks/useToggle";
import { HelpIcon } from "../../../../shared/icons/navbar/HelpIcon";
import { HelpMenu } from "../HelpMenu/HelpMenu";
import { MainMenu } from "../MainMenu/MainMenu";
import { UserAvatar } from "../UserAvatar/UserAvatar";
import UserMenu from "../UserMenu/UserMenu";

import { type NavbarNgProps } from "../../navbar.types";

type MenuType = "main" | "help" | "user";

type Props = {
  ngProps: NavbarNgProps;
  onImpersonationToggle: () => void;
};

const MobileMenu = ({ ngProps, onImpersonationToggle }: Props) => {
  const [menuType, setMenuType] = useState<MenuType>("main");
  const { on: isOpen, setOff: closeDrawer, setOn: openMenu } = useToggle(false);
  const classes = useStyles();

  const { farm, user } = ngProps;

  const closeMenu = () => {
    closeDrawer();
    setMenuType("main");
  };

  const secondaryContent =
    menuType === "help" ? (
      <HelpMenu langId={ngProps.langId} mobile={true} />
    ) : (
      <div>
        <UserMenu
          mobile={true}
          ngProps={ngProps}
          onImpersonationToggle={onImpersonationToggle}
        />
      </div>
    );

  const drawerContent =
    menuType === "main" ? (
      <div className={classes.mainMenu}>
        <div className={classes.toolbar}>
          <div
            aria-label="Open help menu"
            className={classes.toolbarMenuItem}
            id="mobile-help-menu"
            role="button"
            tabIndex={0}
            onClick={() => {
              trackHelpIcon();
              setMenuType("help");
            }}
          >
            <HelpIcon style={{ width: 32, height: 32 }} />
          </div>
          <div
            aria-label="Open user menu"
            className={classes.toolbarMenuItem}
            id="mobile-user-menu"
            onClick={() => setMenuType("user")}
            role="button"
            tabIndex={0}
          >
            <UserAvatar mobile={true} user={user} />
          </div>
        </div>
        <MainMenu
          countryCode={farm.countryCode}
          farmId={farm.id}
          mobile={true}
        />
      </div>
    ) : (
      <div>
        <div className={classes.toolbar}>
          <IconButton
            id="mobile-secondary-menu-close"
            onClick={() => setMenuType("main")}
            size="large"
          >
            <Icon name="close" size="lg" />
          </IconButton>
        </div>
        <div className={classes.userHelpMenuContent}>{secondaryContent}</div>
      </div>
    );

  return (
    <Fragment>
      <div
        aria-label="Open menu"
        className={`${classes.toolbarMenuItem} ${classes.menuButton}`}
        id="mobile-menu-open"
        onClick={openMenu}
        role="button"
        tabIndex={0}
      >
        <MenuIcon style={{ width: 28, height: 28 }} />
      </div>
      <SwipeableDrawer
        anchor="right"
        disableSwipeToOpen={true}
        onClose={closeMenu}
        onOpen={openMenu}
        open={isOpen}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        {drawerContent}
      </SwipeableDrawer>
    </Fragment>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  drawerPaper: {
    width: 275,
  },
  mainMenu: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    height: "100%",
    overflowY: "auto",
  },
  userHelpMenuContent: {
    marginTop: 8,
  },
  menuButton: {
    marginRight: 10,
    marginLeft: 10,
    zIndex: 9999,
  },
  toolbarMenuItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: alpha(theme.palette.common.white, 0.7),
    width: 50,
    "&:hover": {
      color: theme.palette.common.white,
    },
  },
  toolbar: {
    display: "flex",
    justifyContent: "flex-end",
    height: 56,
    alignItems: "center",
    marginRight: 10,
  },
}));

export { MobileMenu };
